import {
  Component,
  OnInit,
  OnDestroy,
  Renderer2,
  HostListener,
} from "@angular/core";
import { Router, NavigationEnd } from "@angular/router";
import { LoginService } from "src/app/services/login.service";
import { DashboardService } from "./../../../services/home/dashboard.service";
import { UserMaster } from "src/app/models/Masters/userMaster";
import { BreadcrumbService } from "src/app/services/breadcrumb.service";
import { ScanFilterStateService } from "src/app/services/scan-filter-state.service";
import { ThemeDef, ThemeService } from "src/app/services/theme.service";
import { isDarkChromePath } from "src/app/shared/dark-chrome";
import { Subscription } from "rxjs";

interface HeaderAlertItem {
  /** Stable id for the read/unread flag - see alertKey(). */
  key: string;
  verificationId: number | string | null;
  label: string;
  subtitle: string;
  color: "red" | "orange" | "purple" | "green" | "yellow" | "blue";
  time: string;
  productName: string | null;
  alertTime: string | null;
  isNew: boolean;
}

@Component({
  selector: "app-header",
  templateUrl: "./header.component.html",
  styleUrls: ["./header.component.css"],
})
export class HeaderComponent implements OnInit, OnDestroy {
  user: UserMaster;
  companyId: number;

  // ── notification bell: Recent Alerts, polled independently of whatever
  // filters the dashboard currently has applied, so it means the same thing
  // from any route ──
  recentAlerts: HeaderAlertItem[] = [];
  alertsLoading = false;
  alertsLoadError = false;
  unreadAlertCount = 0;
  private notifRefreshTimer: any;
  /** Keys (alertKey) of the alerts this user has clicked - per company. An
   *  alert only counts as read once it's been clicked. */
  private static readonly NOTIF_READ_KEY = "sa-notif-read";
  /** Cap on remembered read keys - old alerts fall out of the 7-day window
   *  anyway. */
  private static readonly NOTIF_READ_MAX = 300;
  /** Bell refresh interval - 5 min (was 1 min, which filled the network
   *  log with calls on every page). */
  private static readonly NOTIF_REFRESH_MS = 5 * 60 * 1000;
  /** When the last successful alerts fetch came back (0 = never). */
  private alertsFetchedAt = 0;
  /** A request is in flight - don't stack another on top. */
  private alertsFetching = false;

  private static readonly NOTIF_CACHE_KEY = "sa-notif-cache";

  // ── greeting: "Hi <name>" + a time-of-day message, next to the branding ──
  greeting: string = "";
  private greetingTimer: any;
  private dateRangeSub: Subscription;
  /** Header date button text - driven by ScanFilterStateService.dateRange$
   *  (see syncDateUI) so it can never disagree with the range the pages
   *  actually send to the APIs, e.g. after the dashboard's "Clear all". */
  dateLabel = "Last 7 Days";
  /** The highlighted .date-chip's data-range ("custom" for an applied
   *  custom range). Bound in the template rather than toggled on the DOM, because
   *  the chips sit inside *ngIf and are re-created on navigation. */
  selectedRange: string | null = "last7";
  /** Custom Range inputs' values, bound for the same reason. */
  customDateFrom = "";
  customDateTo = "";

  /** Inline validation shown above the Custom Range Apply button. */
  get customDateError(): string | null {
    const from = this.customDateFrom;
    const to = this.customDateTo;
    if (from && to && from > to) {
      return "From date cannot be later than To date.";
    }
    const today = this.formatDate(new Date());
    if ((from && from > today) || (to && to > today)) {
      return "Dates cannot be in the future.";
    }
    return null;
  }

  // ── dashboard toolbar: Date/Sync/Filters/Export are shown on the
  // dashboard, the analytics drill-down pages, Product Intelligence, and
  // the Generated QR / Inventory reports (each has its own filterable
  // dataset), gated on the current URL via showFiltersButton below. Each
  // page wires its own click-handling for them while it's the routed
  // component (dashboard-new.component.ts / analytics.component.ts /
  // product-intelligence.component.ts / generated-qr-report.component.ts /
  // inventory-report.component.ts) instead of duplicating a second set of
  // controls per page. Only the "Live" badge stays dashboard-only - the
  // others are static drill-downs, not a live feed, so it has no meaning
  // there. ──
  isDashboardRoute: boolean = false;
  isOldDashboardRoute: boolean = false;
  isAnalyticsRoute: boolean = false;
  isProductIntelligenceRoute: boolean = false;
  isGeneratedQrReportRoute: boolean = false;
  isInventoryReportRoute: boolean = false;
  isRecentAlertsRoute: boolean = false;
  private static readonly DASHBOARD_PATH = "/dashboard1";
  private static readonly OLD_DASHBOARD_PATH = "/dashboard";
  private static readonly ANALYTICS_PATH = "/analytics";
  private static readonly PRODUCT_INTELLIGENCE_PATH = "/product-intelligence";
  private static readonly GENERATED_QR_REPORT_PATH = "/generated-qr-report";
  private static readonly INVENTORY_REPORT_PATH = "/inventory-report";
  private static readonly RECENT_ALERTS_PATH = "/recent-alerts";


  private static readonly ROUTE_BREADCRUMBS: Record<string, string[]> = {
    "/masters/usermaster": ["Dashboard", "User Master"],
    "/masters/productmaster": ["Dashboard", "Product Master"],
    "/masters/rolemaster": ["Dashboard", "Role Master"],
    "/masters/productconfiguration": ["Dashboard", "Product Configuration"],
    "/numberactive/productallocation": [
      "Dashboard",
      "Number Activation",
      "Product Allocation",
    ],
    "/transactions/schemedemapping": [
      "Dashboard",
      "Transaction",
      "Product Deallocation",
    ],
    "/transactions/hologramdamage": [
      "Dashboard",
      "Transaction",
      "Hologram Damage",
    ],
    "/reports/verificationrpt": ["Dashboard", "Reports", "Verification Log"],
    "/reports/mappingrpt": ["Dashboard", "Reports", "Mapping Report"],
    "/reports/ratingrpt": ["Dashboard", "Reports", "Rating Log"],
    "/reports/feedbackrpt": ["Dashboard", "Reports", "Feedback Log"],
    "/reports/demappingrpt": ["Dashboard", "Reports", "De-Mapping Log"],
    "/reports/damagerpt": ["Dashboard", "Reports", "Damage Log"],
  };
  /** The trail last set from ROUTE_BREADCRUMBS, so leaving one of those
   *  pages only clears the breadcrumb if it's still ours. */
  private routeBreadcrumb: string[] | null = null;

  /** The navbar breadcrumb shows on internal pages that set one; the pages
   *  listed here render it in their own topbar instead. While it shows, the
   *  greeting is hidden (see .hdr--crumbs in header.component.css). */
  get showHeaderBreadcrumb(): boolean {
    return (
      !this.isDashboardRoute &&
      !this.isAnalyticsRoute &&
      !this.isProductIntelligenceRoute &&
      !this.isGeneratedQrReportRoute &&
      !this.isRecentAlertsRoute &&
      this.breadcrumbService.items.length > 0
    );
  }

  get showFiltersButton(): boolean {
    return (
      this.isDashboardRoute ||
      this.isAnalyticsRoute ||
      this.isProductIntelligenceRoute ||
      this.isGeneratedQrReportRoute ||
      this.isInventoryReportRoute ||
      this.isRecentAlertsRoute
    );
  }

  // ── New/Old dashboard toggle: shown only on the two dashboard routes
  // themselves, letting the user flip between the redesigned dashboard
  // (/dashboard1) and the legacy one (/dashboard) it's replacing. ──
  get showDashboardVersionToggle(): boolean {
    return (
      (this.isDashboardRoute || this.isOldDashboardRoute) &&
      !this.isLoyaltyUser
    );
  }


  get isLoyaltyUser(): boolean {
    const isLoyalty: any = this.user && (this.user as any).isLoyalty;
    return (
      isLoyalty === true ||
      isLoyalty === "true" ||
      isLoyalty === "1" ||
      isLoyalty === 1
    );
  }

  get dashboardVersion(): "new" | "old" {
    return this.isOldDashboardRoute ? "old" : "new";
  }

  switchDashboardVersion(version: "new" | "old"): void {
    const target =
      version === "old"
        ? HeaderComponent.OLD_DASHBOARD_PATH
        : HeaderComponent.DASHBOARD_PATH;
    if (this.router.url.split("?")[0] === target) return;
    this.router.navigate([target]);
  }

  // ── "upgraded dashboard" coach mark ──────────────────────────────────────
  // A one-time nudge pointing at the New/Classic toggle, shown the first
  // time the toggle is visible after login. sessionStorage (not
  // localStorage) is deliberate: LoginService.signOut() clears it, so the
  // tip is "seen" for the rest of this login session but comes back next
  // time someone signs in.
  showUpgradeTip = false;
  private static readonly UPGRADE_TIP_KEY = "sa-dash-upgrade-tip-seen";
  private static readonly UPGRADE_TIP_AUTO_HIDE_MS = 15000;
  private upgradeTipTimer: any;

  private initUpgradeTip(): void {
    if (this.showUpgradeTip || !this.showDashboardVersionToggle) return;
    let seen = false;
    try {
      seen = sessionStorage.getItem(HeaderComponent.UPGRADE_TIP_KEY) === "1";
    } catch (e) {
      /* private mode — just show it for this view, nothing to persist */
    }
    if (seen) return;
    this.showUpgradeTip = true;
    // Auto-hide after 15s if the person hasn't closed it themselves -
    // dismissUpgradeTip() below clears this timer on an explicit close.
    this.upgradeTipTimer = setTimeout(
      () => this.dismissUpgradeTip(),
      HeaderComponent.UPGRADE_TIP_AUTO_HIDE_MS,
    );
  }

  // Marked seen only once the person actually closes it via its own X, or
  // once the 15s auto-hide fires — not the moment it renders (an unrelated
  // reload could burn the one-time showing before anyone saw it) and not
  // on an outside click or the CTA (the tip should stay put until then).
  dismissUpgradeTip(ev?: Event): void {
    if (ev) ev.stopPropagation();
    this.showUpgradeTip = false;
    clearTimeout(this.upgradeTipTimer);
    try {
      sessionStorage.setItem(HeaderComponent.UPGRADE_TIP_KEY, "1");
    } catch (e) {}
  }

  /** The tip's own CTA — take them to the new dashboard. Doesn't dismiss the
   *  tip itself; only its own close button does that. */
  goToNewDashboardFromTip(ev?: Event): void {
    if (ev) ev.stopPropagation();
    this.switchDashboardVersion("new");
  }

  get showFiltersTrigger(): boolean {
    // Recent Alerts has only a date range + search, no filter drawer.
    return (
      this.showFiltersButton &&
      !this.isProductIntelligenceRoute &&
      !this.isRecentAlertsRoute
    );
  }

  /**
   * Which popover is open, or null. A single field rather than one boolean per
   * popover: it makes "only one open at a time" structural instead of something
   * every new toggle has to remember to enforce.
   */
  openPop: "theme" | "notif" | "date" | null = null;

  /** Kept for anything outside this component that still reads it. */
  get showNotificationBar(): boolean {
    return this.openPop === "notif";
  }

  // ── colour theme: owned by ThemeService (shared with the Configuration
  // page's Theme Settings tab); these just expose it to the template ──
  get themes(): ThemeDef[] {
    return this.themeService.themes;
  }
  get activeTheme(): string {
    return this.themeService.activeTheme;
  }
  /** Any component may listen for this to re-render canvas/SVG that samples the palette. */
  static readonly THEME_EVENT = ThemeService.THEME_EVENT;

  private static readonly CONFIGURATION_PATH = "/configuration";
  isConfigurationRoute = false;

  /** Dark bar only where the page under it is dark too: Dark appearance on
   *  one of the pages in app/shared/dark-chrome.ts (the same list
   *  default-layout uses for its dark backdrop). Everything else stays light
   *  whatever the appearance. */
  get isDarkHeader(): boolean {
    return !this.themeService.isLight && isDarkChromePath(this.router.url);
  }

  constructor(
    private loginService: LoginService,
    private dashboardService: DashboardService,
    private render: Renderer2,
    private router: Router,
    public breadcrumbService: BreadcrumbService,
    private scanFilterState: ScanFilterStateService,
    private themeService: ThemeService,
  ) {
    this.user = this.loginService.user();

    // Applied in the constructor, not ngOnInit: the header is created before the
    // routed component, so the tokens are on <html> before anything paints and
    // there is no flash of the default palette.
    this.themeService.restoreTheme();
  }

  ngOnInit() {
    this.companyId = this.user.compId;

    this.updateGreeting();
    this.greetingTimer = setInterval(() => this.updateGreeting(), 60000);

    this.updateIsDashboardRoute(this.router.url);
    this.initUpgradeTip();
    this.router.events.subscribe((e) => {
      if (e instanceof NavigationEnd) {
        this.updateIsDashboardRoute(e.urlAfterRedirects);
        this.initUpgradeTip();
      }
    });
    this.setupDateFilter();
    // BehaviorSubject - emits the current range immediately, then on every
    // change from anywhere (this header, dashboard "Clear all", login reset).
    this.dateRangeSub = this.scanFilterState.dateRange$.subscribe((range) =>
      this.syncDateUI(range.dateFrom, range.dateTo, range.datePreset),
    );

    const cached = this.readCachedAlerts();
    if (cached) {
      this.applyRecentAlerts(cached.list);
      this.alertsFetchedAt = cached.fetchedAt;
    } else {
      this.fetchRecentAlerts();
    }
    // Background refresh: skipped while the tab is hidden; coming back to
    // the tab (or opening the bell) catches up if the list has gone stale.
    this.notifRefreshTimer = setInterval(() => {
      if (!document.hidden) this.fetchRecentAlerts();
    }, HeaderComponent.NOTIF_REFRESH_MS);
    document.addEventListener("visibilitychange", this.onVisibilityChange);
  }

  private readonly onVisibilityChange = (): void => {
    if (!document.hidden) this.refreshAlertsIfStale();
  };

  /** Fetch only when the last successful fetch is older than the refresh
   *  interval - e.g. returning to the tab, or opening the bell. */
  private refreshAlertsIfStale(): void {
    if (Date.now() - this.alertsFetchedAt >= HeaderComponent.NOTIF_REFRESH_MS) {
      this.fetchRecentAlerts();
    }
  }

  ngOnDestroy(): void {
    clearInterval(this.greetingTimer);
    clearInterval(this.notifRefreshTimer);
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
    clearTimeout(this.upgradeTipTimer);
    // Both registered in setupDateFilter() below - see its comment for why
    // these are plain document-level listeners rather than template
    // (click) bindings. HeaderComponent is expected to stay mounted for the
    // app's whole lifetime today, so this mostly guards against surprises
    // (hot-reload, a future refactor that makes the header conditional)
    // rather than a bug seen in practice - but leaving them unremoved would
    // be the same class of cross-page listener leak analytics.component.ts
    // and dashboard-new.component.ts both take care to avoid.
    document.removeEventListener("click", this.handleDateClick, true);
    document.removeEventListener("click", this.handleDateApplyClick, true);
    if (this.dateRangeSub) this.dateRangeSub.unsubscribe();
  }

  // ── greeting ─────────────────────────────────────────────────────────────
  private updateGreeting(): void {
    const hour = new Date().getHours();
    if (hour < 12) {
      this.greeting = "Good morning";
    } else if (hour < 17) {
      this.greeting = "Good afternoon";
    } else {
      this.greeting = "Good evening";
    }
  }
  private readonly PRESET_LABELS: { [key: string]: string } = {
    today: "Today",
    yesterday: "Yesterday",
    thisWeek: "This Week",
    lastWeek: "Last Week",
    last7: "Last 7 Days",
    last30: "Last 30 Days",
    thisMonth: "This Month",
    lastMonth: "Last Month",
    custom: "Custom Range",
  };

  /** Mirrors the shared date range onto the header: button label, the
   *  highlighted preset chip and the Custom Range inputs. */
  private syncDateUI(
    dateFrom: string | null,
    dateTo: string | null,
    datePreset: string | null,
  ): void {
    this.dateLabel = datePreset || "Last 7 Days";
    const rangeKey = Object.keys(this.PRESET_LABELS).find(
      (k) => k !== "custom" && this.PRESET_LABELS[k] === datePreset,
    );
    // Any non-preset label is an applied custom range.
    this.selectedRange = rangeKey || (datePreset ? "custom" : "last7");
    this.customDateFrom = dateFrom || "";
    this.customDateTo = dateTo || "";
  }

  private computePresetRange(range: string): { from: Date; to: Date } {
    const to = new Date();
    let from = new Date();

    if (range === "today") {
      from = new Date();
    } else if (range === "yesterday") {
      from.setDate(to.getDate() - 1);
      to.setDate(to.getDate() - 1);
    } else if (range === "thisWeek") {
      // Week starts Monday, same "from the start of the period through
      // today" shape as thisMonth below.
      const day = to.getDay();
      const diffToMonday = day === 0 ? 6 : day - 1;
      from = new Date(
        to.getFullYear(),
        to.getMonth(),
        to.getDate() - diffToMonday,
      );
    } else if (range === "lastWeek") {
      const day = to.getDay();
      const diffToMonday = day === 0 ? 6 : day - 1;
      const thisMonday = new Date(
        to.getFullYear(),
        to.getMonth(),
        to.getDate() - diffToMonday,
      );
      const lastMonday = new Date(
        thisMonday.getFullYear(),
        thisMonday.getMonth(),
        thisMonday.getDate() - 7,
      );
      const lastSunday = new Date(
        thisMonday.getFullYear(),
        thisMonday.getMonth(),
        thisMonday.getDate() - 1,
      );

      return { from: lastMonday, to: lastSunday };
    } else if (range === "last7") {
      // -6, not -7: an inclusive 7-day window (today back through 6 days
      // ago), same "N-1" shape as last30's -29 below - matches
      // ScanFilterStateService's own defaultDateRange().
      from.setDate(to.getDate() - 6);
    } else if (range === "last30") {
      from.setDate(to.getDate() - 29);
    } else if (range === "thisMonth") {
      from = new Date(to.getFullYear(), to.getMonth(), 1);
    } else if (range === "lastMonth") {
      from = new Date(to.getFullYear(), to.getMonth() - 1, 1);

      return {
        from,
        to: new Date(to.getFullYear(), to.getMonth(), 0),
      };
    }

    return { from, to };
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  }
  private setupDateFilter(): void {
    document.addEventListener("click", this.handleDateClick, true);
    // Handles the Custom Range popover's own Apply button - separate from
    // handleDateClick above (which only handles the preset/custom .date-chip
    // buttons themselves and, for the "custom" chip, just reveals the
    // #dateCustomFields inputs). Without this, picking "Custom Range" opens
    // the From/To fields but nothing ever commits them - the person could
    // fill in dates and click Apply and nothing would happen.
    document.addEventListener("click", this.handleDateApplyClick, true);
  }
  private handleDateClick = (e: Event): void => {
    const target = e.target as HTMLElement;

    const chip = target.closest(".date-chip") as HTMLElement | null;

    if (chip) {
      e.stopPropagation();

      const range = chip.dataset.range;

      if (!range) {
        return;
      }

      this.selectedRange = range;

      if (range === "custom") {
        const customFields = document.getElementById("dateCustomFields");

        if (customFields) {
          customFields.classList.add("open");
        }

        return;
      }

      const customFields = document.getElementById("dateCustomFields");

      if (customFields) {
        customFields.classList.remove("open");
      }

      const { from, to } = this.computePresetRange(range);

      const dateFrom = this.formatDate(from);
      const dateTo = this.formatDate(to);

      const datePreset = this.PRESET_LABELS[range] || "Custom Range";

      // THIS is the important line
      // Label, chip highlight and From/To inputs follow via syncDateUI().
      this.scanFilterState.setDateRange(dateFrom, dateTo, datePreset);

      window.dispatchEvent(new CustomEvent("sa-date-applied"));
    }
  };

  /** The Custom Range popover's own Apply button (#dateApplyBtn), reading
   *  the #topDateFrom/#topDateTo inputs directly - same raw-DOM style as
   *  handleDateClick above, and the same validation dashboard-new.component.ts
   *  used to do itself before date became a shared, header-owned concern:
   *  both fields required, From must not be after To, and neither may be
   *  in the future. Invalid input just leaves the popover open rather than
   *  silently discarding what was typed. */
  private handleDateApplyClick = (e: Event): void => {
    const target = e.target as HTMLElement;
    const applyBtn = target.closest("#dateApplyBtn") as HTMLElement | null;

    if (!applyBtn) {
      return;
    }

    e.stopPropagation();

    const topDateFrom = document.getElementById(
      "topDateFrom",
    ) as HTMLInputElement | null;
    const topDateTo = document.getElementById(
      "topDateTo",
    ) as HTMLInputElement | null;

    if (!topDateFrom || !topDateTo || !topDateFrom.value || !topDateTo.value) {
      return;
    }

    if (topDateFrom.value > topDateTo.value) {
      return;
    }

    const todayStr = this.formatDate(new Date());
    if (topDateFrom.value > todayStr || topDateTo.value > todayStr) {
      return;
    }

    const datePreset = `${topDateFrom.value} → ${topDateTo.value}`;

    this.scanFilterState.setDateRange(
      topDateFrom.value,
      topDateTo.value,
      datePreset,
    );

    // syncDateUI keeps the "Custom Range…" chip selected for it.

    window.dispatchEvent(new CustomEvent("sa-date-applied"));
  };

  private updateIsDashboardRoute(url: string): void {
    const path = url.split("?")[0];
    this.isDashboardRoute = path === HeaderComponent.DASHBOARD_PATH;
    this.isOldDashboardRoute = path === HeaderComponent.OLD_DASHBOARD_PATH;
    // Prefix match, not exact - covers /analytics/:type too (the
    // Authentic/Suspected/Duplicate/Invalid drill-down pages, see
    // app-routing.module.ts), which all share this same toolbar.
    this.isAnalyticsRoute =
      path === HeaderComponent.ANALYTICS_PATH ||
      path.startsWith(HeaderComponent.ANALYTICS_PATH + "/");
    this.isProductIntelligenceRoute =
      path === HeaderComponent.PRODUCT_INTELLIGENCE_PATH;
    // Prefix match - covers /generated-qr-report/:type too (the
    // Assigned/Scrapped/Deallocated/In-Stock drill-downs, see
    // app-routing.module.ts), same reasoning as isAnalyticsRoute above.
    this.isGeneratedQrReportRoute =
      path === HeaderComponent.GENERATED_QR_REPORT_PATH ||
      path.startsWith(HeaderComponent.GENERATED_QR_REPORT_PATH + "/");
    this.isInventoryReportRoute =
      path === HeaderComponent.INVENTORY_REPORT_PATH;
    this.isRecentAlertsRoute = path === HeaderComponent.RECENT_ALERTS_PATH;
    this.isConfigurationRoute = path === HeaderComponent.CONFIGURATION_PATH;
    this.updateRouteBreadcrumb(path);
  }

  private updateRouteBreadcrumb(path: string): void {
    const crumbs = HeaderComponent.ROUTE_BREADCRUMBS[path.toLowerCase()];
    if (crumbs) {
      this.breadcrumbService.items = crumbs;
      this.routeBreadcrumb = crumbs;
    } else if (this.routeBreadcrumb) {
      if (this.breadcrumbService.items === this.routeBreadcrumb) {
        this.breadcrumbService.items = [];
      }
      this.routeBreadcrumb = null;
    }
  }

  /**
   * Every crumb but the last one, in every breadcrumb this app currently
   * sets, points back to the dashboard - so rather than have each routed
   * page hand the header a click target per crumb, the header just always
   * sends non-final crumbs there.
   */
  goToDashboard(): void {
    this.router.navigate([HeaderComponent.DASHBOARD_PATH]);
  }

  // ── theme ────────────────────────────────────────────────────────────────
  applyTheme(name: string, persist: boolean = true): void {
    // The quick menu keeps its long-standing behaviour: each theme brings
    // its own light/dark appearance (Classic light, the rest dark).
    this.themeService.applyTheme(name, persist, persist);
    // Chosen -> dismiss. Not on the initial restore, which passes persist=false.
    if (persist) {
      this.openPop = null;
    }
  }

  /** Preserved entry point — now routes through the shared popover state. */
  showNotification() {
    this.openPop = this.openPop === "notif" ? null : "notif";
  }

  togglePop(which: "theme" | "notif" | "date", ev?: Event): void {
    // Without this the document listener below would see the same click bubble
    // up and immediately close what we just opened.
    if (ev) {
      ev.stopPropagation();
    }
    const opening = this.openPop !== which;
    this.openPop = opening ? which : null;
    // Opening the bell doesn't mark anything read - each alert is marked
    // read only when it's clicked (see goToRecentAlert).
    if (opening && which === "notif") this.refreshAlertsIfStale();
  }

  closePops(): void {
    this.openPop = null;
  }

  /** Click anywhere else dismisses. The popovers stop their own clicks.
   *  The upgrade tip is deliberately NOT included here — it should only
   *  go away via its own close button, not an incidental outside click. */
  @HostListener("document:click")
  onDocumentClick(): void {
    if (this.openPop) {
      this.closePops();
    }
  }

  @HostListener("document:keydown.escape")
  onEscape(): void {
    if (this.openPop) {
      this.closePops();
    }
  }

  /**
   * The date popover's own preset/custom-range wiring is plain JS living in
   * this component (handleDateClick/handleDateApplyClick above), so it can't
   * just call `this.openPop = null` directly from those handlers. It
   * dispatches this event instead once a date range is actually applied,
   * and the popover closes the same way clicking outside it would.
   */
  @HostListener("window:sa-date-applied")
  onDateApplied(): void {
    if (this.openPop === "date") {
      this.closePops();
    }
  }

  // ── notification bell: Recent Alerts ─────────────────────────────────────

  private alertMetaFromRemarks(remarks: string): {
    label: string;
    subtitle: string;
    color: HeaderAlertItem["color"];
  } {
    const r = (remarks || "").toLowerCase().trim();
    if (!r) {
      return {
        label: "Unverified Scan",
        subtitle: "QR not activated",
        color: "purple",
      };
    }
    if (
      r.includes("not a valid") ||
      r.includes("counterfeit") ||
      r.includes("fake") ||
      r === "invalid product" ||
      r === "qr code not mapped"
    ) {
      return {
        label: "Fake Product",
        subtitle: "Suspected counterfeit",
        color: "red",
      };
    }
    if (r === "already scanned" || r.includes("multiple")) {
      return {
        label: "Duplicate Scan",
        subtitle: "Code scanned multiple times",
        color: "yellow",
      };
    }
    if (r === "genuine product" || r === "verified") {
      return {
        label: "Genuine Scan",
        subtitle: "Verified authentic product",
        color: "green",
      };
    }
    if (r.includes("not activated") || r.includes("unverified")) {
      return {
        label: "Unverified Scan",
        subtitle: "QR not activated",
        color: "purple",
      };
    }
    return { label: remarks, subtitle: "", color: "blue" };
  }

  /** Same as dashboard-new's alertVisualsFromStatus - the status decides
   *  the icon first (e.g. "Potential Fake Detected" -> red, whatever its
   *  remarks say); null falls back to the remarks-based colour. */
  private colorFromStatus(status: string): HeaderAlertItem["color"] | null {
    const s = (status || "").toLowerCase();
    if (s.includes("fake") || s.includes("counterfeit")) return "red";
    if (s.includes("duplicate")) return "yellow";
    // "unverified" contains "verified" - check it first.
    if (s.includes("unverified")) return "purple";
    if (s.includes("genuine") || s.includes("verified")) return "green";
    return null;
  }

  private relTimeFromIso(iso: string | null): string {
    if (!iso) return "";
    const t = new Date(iso).getTime();
    if (isNaN(t)) return "";
    const secs = Math.round((Date.now() - t) / 1000);
    if (secs < 5) return "just now";
    if (secs < 60) return secs + " sec ago";
    const mins = Math.floor(secs / 60);
    if (mins < 60) return mins + " min ago";
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return hrs + "h ago";
    return Math.floor(hrs / 24) + "d ago";
  }

  /** Reads the cached alert list back if it's still within NOTIF_REFRESH_MS -
   *  otherwise (missing, unparsable, or stale) returns null so the caller
   *  falls back to fetching fresh. */
  private readCachedAlerts(): { list: any[]; fetchedAt: number } | null {
    try {
      const raw = sessionStorage.getItem(HeaderComponent.NOTIF_CACHE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (
        !parsed ||
        !Array.isArray(parsed.list) ||
        typeof parsed.fetchedAt !== "number" ||
        Date.now() - parsed.fetchedAt > HeaderComponent.NOTIF_REFRESH_MS
      ) {
        return null;
      }
      return { list: parsed.list, fetchedAt: parsed.fetchedAt };
    } catch (e) {
      return null;
    }
  }

  private writeCachedAlerts(list: any[]): void {
    try {
      sessionStorage.setItem(
        HeaderComponent.NOTIF_CACHE_KEY,
        JSON.stringify({ list, fetchedAt: Date.now() }),
      );
    } catch (e) {
      /* private mode / storage full — just skip caching for this fetch */
    }
  }

  private fetchRecentAlerts(): void {
    // The new-dashboard APIs shouldn't be called while the old, legacy
    // dashboard (/dashboard) is the active route. The interval timer that
    // drives this (see ngOnInit) keeps running regardless, so it just picks
    // back up as soon as the route changes away from there.
    if (this.isOldDashboardRoute) return;

    const compId = this.user && this.user.compId ? Number(this.user.compId) : null;
    if (!compId) return;

    // Deliberately its own fixed last-7-days window, not
    // ScanFilterStateService's - the bell should mean the same thing
    // regardless of whatever date range the dashboard currently has
    // applied. Local dates (toISOString would shift to UTC).
    const fmt = (d: Date) =>
      d.getFullYear() +
      "-" +
      String(d.getMonth() + 1).padStart(2, "0") +
      "-" +
      String(d.getDate()).padStart(2, "0");
    const to = new Date();
    const from = new Date();
    from.setDate(to.getDate() - 6);
    const payload = {
      compId,
      fromDt: fmt(from),
      toDt: fmt(to),
      productId: null,
      status: null,
    };

    if (this.alertsFetching) return;
    this.alertsFetching = true;
    this.alertsLoading = this.recentAlerts.length === 0;
    // The bell's alerts come only from GetNewDashboardData's
    // details.recentAlerts.
    this.dashboardService.getNewDashboardData(payload).subscribe(
      (res: any) => {
        this.alertsFetching = false;
        this.alertsLoading = false;
        const ok =
          res && (res.responseCode === "200" || res.responseCode === 200);
        if (!ok) {
          this.alertsLoadError = this.recentAlerts.length === 0;
          return;
        }
        this.alertsLoadError = false;
        this.alertsFetchedAt = Date.now();
        const list =
          res.details && Array.isArray(res.details.recentAlerts)
            ? res.details.recentAlerts
            : [];
        this.applyRecentAlerts(list);
        this.writeCachedAlerts(list);
      },
      () => {
        this.alertsFetching = false;
        this.alertsLoading = false;
        this.alertsLoadError = this.recentAlerts.length === 0;
      },
    );
  }

  /** How many of the newest alerts the bell shows. */
  private static readonly NOTIF_LIMIT = 10;

  /** One alert's id for its read flag - same alert, same key across
   *  refreshes. */
  private alertKey(a: any): string {
    return [
      a.verificationId != null ? a.verificationId : "",
      a.qrData || "",
      a.alertTime || "",
      a.status || a.remarks || "",
    ].join("|");
  }

  /** Read keys are per company - two companies' alerts never share state. */
  private readStorageKey(): string {
    const compId = this.user && this.user.compId ? this.user.compId : "";
    return `${HeaderComponent.NOTIF_READ_KEY}-${compId}`;
  }

  private readKeys(): string[] {
    try {
      const list = JSON.parse(localStorage.getItem(this.readStorageKey()) || "[]");
      return Array.isArray(list) ? list : [];
    } catch (e) {
      return [];
    }
  }

  private applyRecentAlerts(list: any[]): void {
    const read = new Set(this.readKeys());

    this.recentAlerts = list
      .slice()
      .sort((a: any, b: any) => {
        const ta = a.alertTime ? new Date(a.alertTime).getTime() : 0;
        const tb = b.alertTime ? new Date(b.alertTime).getTime() : 0;
        return tb - ta;
      })
      .slice(0, HeaderComponent.NOTIF_LIMIT)
      .map((a: any) => {
        const meta = this.alertMetaFromRemarks(a.remarks);
        const key = this.alertKey(a);
        return {
          key,
          verificationId: a.verificationId != null ? a.verificationId : null,
          label: a.status || meta.label,
          subtitle: a.remarks || meta.subtitle,
          color: this.colorFromStatus(a.status) || meta.color,
          time: this.relTimeFromIso(a.alertTime),
          productName: a.productName || null,
          alertTime: a.alertTime || null,
          isNew: !read.has(key),
        };
      });

    this.unreadAlertCount = this.recentAlerts.filter((a) => a.isNew).length;
  }

  /** Marks one alert read - the badge drops by one. */
  private markAlertRead(item: HeaderAlertItem): void {
    if (!item.isNew) return;
    item.isNew = false;
    this.unreadAlertCount = Math.max(0, this.unreadAlertCount - 1);
    const keys = this.readKeys().filter((k) => k !== item.key);
    keys.push(item.key);
    try {
      localStorage.setItem(
        this.readStorageKey(),
        JSON.stringify(keys.slice(-HeaderComponent.NOTIF_READ_MAX)),
      );
    } catch (e) {}
  }

  /**
   * Notification click -> dashboard's Recent Alerts. Works from any route:
   * navigates to the dashboard first if we're not already there, then asks
   * it (via a window event it listens for) to scroll/highlight the section
   * and, when the same alert can still be found in its own data, open that
   * alert's detail drawer directly.
   */
  goToRecentAlert(item?: HeaderAlertItem): void {
    this.closePops();
    if (item) this.markAlertRead(item);
    const verificationId = item ? item.verificationId : null;
    const focus = () =>
      window.dispatchEvent(
        new CustomEvent("sa-focus-alert", { detail: { verificationId } }),
      );
    if (this.isDashboardRoute) {
      focus();
    } else {
      this.router
        .navigate([HeaderComponent.DASHBOARD_PATH])
        .then(() => setTimeout(focus, 300));
    }
  }

  logout() {
    this.loginService.signOut();
  }
}
